/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from "@material-ui/core";
import { useDispatch } from "react-redux";
import { cancelMyEvent } from "../../../../redux/bookings/actions";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ParticipationStatus } from "../../../../models/enums/participationStatus.enum";
import { TrainingType } from "../../../../models/enums/trainingType.enum";
import createSafeHTML from "../../../../utils/createSafeHTML";
import { getCurrentLanguage } from "../../../../models/enums/salutationType.enum";

interface CancelButtonProps {
  bookingId: string;
  type: TrainingType.DefaultEvent | TrainingType.ELearning;
  createdAt: Date;
  status: number;
  elearning_started_count?: number;
  eventDate?: Date;
}

const CancelEventButton: React.FC<CancelButtonProps> = ({
  bookingId,
  type,
  createdAt,
  status,
  elearning_started_count = 0,
  eventDate,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("common");
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  const calculateDaysDifference = (startDate: Date, endDate: Date): number => {
    const timeDifference = endDate.getTime() - startDate.getTime();
    return Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  };

  const daysSinceBooking = calculateDaysDifference(new Date(createdAt), new Date());
  const daysUntilEvent = eventDate
    ? calculateDaysDifference(new Date(), new Date(eventDate))
    : undefined;

  const fetchMetaData = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/meta-data");
      const isGerman = getCurrentLanguage() === "de" || getCurrentLanguage() === "de-DE";
      setConfirmationMessage(
        daysSinceBooking <= 14
          ? isGerman
            ? data.cancellation_message_under_14_days_german
            : data.cancellation_message_under_14_days_english
          : isGerman
            ? data.cancellation_message_over_14_days_german
            : data.cancellation_message_over_14_days_english
      );
    } catch {
      setConfirmationMessage(t("cancellation.cancelError"));
    }
  }, [i18n.language, daysSinceBooking, t]);

  useEffect(() => {
    fetchMetaData();
  }, [fetchMetaData]);

  const isCancellationDisabled =
    status === ParticipationStatus.Cancelled ||
    status === ParticipationStatus.CancelledByStaff ||
    (type === TrainingType.ELearning && elearning_started_count >= 1) ||
    (daysUntilEvent !== undefined && daysUntilEvent < 3) ||
    status === ParticipationStatus.Participated;

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleConfirmCancellation = () => {
    dispatch(cancelMyEvent(bookingId));
    handleCloseDialog();
  };

  return (
    <Grid item xs={12} style={{ marginTop: "1.5rem" }} data-cy="cancel-button-container">
      {isCancellationDisabled ? (
        <Tooltip
          title={
            <span data-cy="cancel-tooltip">
              {t("cancellation.cancelMessage", { ns: "common" })}{" "}
              <Link
                href="/agb"
                target="_blank"
                rel="noopener noreferrer"
                data-cy="cancel-agb-link"
              >
                {t("cancellation.agb")}
              </Link>
            </span>
          }
          interactive
          arrow
        >
          <span data-cy="disabled-button-container">
            <Button
              size="medium"
              variant="contained"
              color="primary"
              disabled
              data-cy="cancel-button-disabled"
            >
              {t("cancellation.cancel", { ns: "common" })}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          size="medium"
          variant="contained"
          color="primary"
          onClick={handleOpenDialog}
          data-cy="cancel-button-enabled"
        >
          {t("cancellation.cancel", { ns: "common" })}
        </Button>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} data-cy="cancel-dialog">
        <DialogTitle data-cy="cancel-dialog-title">
          {t("cancellation.confirmCancellation", { ns: "common" })}
        </DialogTitle>
        <DialogContent
          data-cy="cancel-dialog-content"
          dangerouslySetInnerHTML={createSafeHTML(confirmationMessage)}
        />
        <DialogActions data-cy="cancel-dialog-actions">
          <Button
            onClick={handleCloseDialog}
            color="secondary"
            data-cy="cancel-dialog-cancel"
          >
            {t("forms.buttons.cancel", { ns: "common" })}
          </Button>
          <Button
            onClick={handleConfirmCancellation}
            color="primary"
            data-cy="cancel-dialog-confirm"
          >
            {t("forms.buttons.confirm", { ns: "common" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default CancelEventButton;
